/**
 * Copy deterrents for the SSC Rankers League exam screen. Honest scope: no web app can fully stop
 * copying (a second phone can photograph the screen), so these raise the effort, and every
 * question carries a faint watermark with the student's ID so a leak can be traced. Telegram's
 * Mini App API has no screenshot-blocking switch, so none is used (see tma.ts).
 */

/** The parts of a keyboard event the shortcut check reads. */
interface KeyLike {
  key: string;
  ctrlKey?: boolean;
  metaKey?: boolean;
  shiftKey?: boolean;
  altKey?: boolean;
}

/** Ctrl/Cmd + these letters: copy, cut, paste, select all, print, save, view source. */
const BLOCKED_WITH_MOD = new Set(['c', 'x', 'v', 'a', 'p', 's', 'u']);

/** Ctrl+Shift + these: browser developer tools. */
const BLOCKED_DEVTOOLS = new Set(['i', 'j', 'c']);

export function isBlockedKey(e: KeyLike): boolean {
  const key = e.key.toLowerCase();
  if (key === 'printscreen' || key === 'snapshot') return true;
  if (key === 'f12') return true;
  const mod = !!(e.ctrlKey || e.metaKey);
  if (mod && BLOCKED_WITH_MOD.has(key)) return true;
  if (mod && e.shiftKey && BLOCKED_DEVTOOLS.has(key)) return true;
  // Ctrl+Insert (copy) and Shift+Insert (paste), Shift+Delete (cut).
  if (key === 'insert' && (mod || e.shiftKey)) return true;
  if (key === 'delete' && e.shiftKey) return true;
  return false;
}

const isPrintScreen = (e: KeyLike): boolean => {
  const key = e.key.toLowerCase();
  return key === 'printscreen' || key === 'snapshot';
};

/** The real `document` (or any `EventTarget`, as in tests). */
type ProtectTarget = Pick<EventTarget, 'addEventListener' | 'removeEventListener'>;

interface ProtectOptions {
  /** Called when something was blocked (the screen shows a short notice). */
  onBlocked?: (what: BlockedAction) => void;
  /** Called on PrintScreen: best effort to clear the clipboard. */
  clearClipboard?: () => void;
}

export type BlockedAction = 'copy' | 'cut' | 'paste' | 'select' | 'menu' | 'drag' | 'print' | 'key';

/** Events that are simply cancelled, and the notice each one raises. */
const CANCELLED: [string, BlockedAction][] = [
  ['copy', 'copy'],
  ['cut', 'cut'],
  ['paste', 'paste'],
  ['contextmenu', 'menu'],
  ['dragstart', 'drag'],
  ['drag', 'drag'],
  ['drop', 'drag'],
  ['selectstart', 'select'],
  ['beforeprint', 'print'],
];

/**
 * Block selection, copy, cut, paste, right-click, drag, print and the usual shortcuts on
 * `target` (capture phase, so nothing inside the exam can re-enable them). Returns the cleanup.
 */
export function installProtection(target: ProtectTarget, opts: ProtectOptions = {}): () => void {
  const off: (() => void)[] = [];
  const add = (type: string, fn: (e: Event) => void) => {
    target.addEventListener(type, fn, { capture: true });
    off.push(() => target.removeEventListener(type, fn, { capture: true }));
  };
  for (const [type, what] of CANCELLED)
    add(type, (e) => {
      e.preventDefault();
      e.stopPropagation();
      opts.onBlocked?.(what);
    });
  add('keydown', (ev) => {
    const e = ev as Event & KeyLike;
    if (!isBlockedKey(e)) return;
    e.preventDefault();
    e.stopPropagation();
    if (isPrintScreen(e)) opts.clearClipboard?.();
    opts.onBlocked?.(isPrintScreen(e) ? 'print' : 'key');
  });
  // PrintScreen is often only reported on key release.
  add('keyup', (ev) => {
    const e = ev as Event & KeyLike;
    if (!isPrintScreen(e)) return;
    e.preventDefault();
    opts.clearClipboard?.();
    opts.onBlocked?.('print');
  });
  return () => {
    for (const f of off) f();
  };
}

// ── Watermark ───────────────────────────────────────────────────────────────────────────────

const xmlEscape = (s: string): string =>
  s.replace(/[&<>"']/g, (c) => `&#${String(c.charCodeAt(0))};`);

/**
 * A faint, tiled, rotated text tile as a CSS `background-image` value. The text is the student's
 * ID (and the short attempt code), repeated across the whole question.
 */
export function watermarkBackground(label: string): string {
  const text = xmlEscape(label.slice(0, 40));
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="220" height="150">` +
    `<text x="110" y="75" text-anchor="middle" font-family="sans-serif" font-size="15" ` +
    `font-weight="700" fill="#808080" fill-opacity="0.16" transform="rotate(-24 110 75)">${text}</text></svg>`;
  return `url("data:image/svg+xml;utf8,${encodeURIComponent(svg)}")`;
}

/** "ID 123456789 · A1B2C3D4": the learner's Telegram ID plus the short attempt code. */
export function watermarkLabel(userId: number | null, code: string | null): string {
  const parts = [userId ? `ID ${String(userId)}` : null, code].filter(Boolean);
  return parts.length ? parts.join(' · ') : 'SSCExamPrep';
}

// ── Leaving the exam screen ─────────────────────────────────────────────────────────────────

/** The server accepts at most this many switches and this many hidden ms per report. */
const MAX_SWITCHES = 5;

const MAX_HIDDEN_MS = 600_000;

interface AwayReport {
  switches: number;
  hiddenMs: number;
}

/**
 * Counts how often (and how long) the student left the exam screen. Reports are drained in
 * batches the server accepts; a failed send is put back so nothing is lost.
 */
export class AwayTracker {
  private hiddenAt: number | null = null;
  private switches = 0;
  private hiddenMs = 0;

  hide(nowMs: number): void {
    this.hiddenAt ??= nowMs;
  }

  /** Back on screen. Returns true when this ended an absence. */
  show(nowMs: number): boolean {
    if (this.hiddenAt === null) return false;
    this.switches += 1;
    this.hiddenMs += Math.max(0, nowMs - this.hiddenAt);
    this.hiddenAt = null;
    return true;
  }

  get pending(): boolean {
    return this.switches > 0 || this.hiddenMs > 0;
  }

  /** The next batch to send (null when nothing is waiting). */
  take(): AwayReport | null {
    if (!this.pending) return null;
    const switches = Math.min(MAX_SWITCHES, this.switches);
    const hiddenMs = Math.min(MAX_HIDDEN_MS, Math.trunc(this.hiddenMs));
    this.switches -= switches;
    this.hiddenMs -= hiddenMs;
    return { switches, hiddenMs };
  }

  restore(r: AwayReport): void {
    this.switches += r.switches;
    this.hiddenMs += r.hiddenMs;
  }
}

// ── Device hint ─────────────────────────────────────────────────────────────────────────────

/** SHA-256 hex of `text` (the optional `device` field; we never send anything readable). */
export async function sha256Hex(text: string): Promise<string> {
  const bytes = new TextEncoder().encode(text);
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}
